/* istanbul ignore file */

import { PluginObject } from 'vue';
import Vuex, { Store } from 'vuex';
import createPersistedState from 'vuex-persistedstate';

import { Config } from '@/environment';

import { makeCmsModule, CmsModuleState } from './cms';
import { UiModuleState, makeUiModule } from './ui';
import { FindAChipotleModuleState, makeFindAChipotleModule } from './find-a-chipotle';
import { makeMenuModule, MenuModuleState, MUTATIONS as MENU_MUTATIONS } from './menu';
import { makeCustomerModule, CustomerModuleState, MUTATIONS as CUSTOMER_MUTATIONS } from './customer';
import { RestaurantModuleState, makeRestaurantModule, MUTATIONS as RESTAURANT_MUTATIONS } from './restaurant';
import { MealBuilderModuleState, makeMealBuilderModule } from './meal-builder';
import { OrderModuleState, makeOrderModule, MUTATIONS as ORDER_MUTATIONS } from './order';
import { MUTATIONS as CHECKOUT_MUTATIONS } from './checkout';
import { freezeMenu, DeepPartial } from '@/helpers';
import { CheckoutModuleState, makeCheckoutModule } from './checkout';
import { RetroCreditModuleState, makeRetroCreditModule } from './retro-credit';
import { RewardModuleState, makeRewardModule } from './reward';
import { makeFeatureFlagsModule, FeatureFlagsModuleState } from './feature-flags';
import { makeAdobeModule, AdobeModuleState, MUTATIONS as ADOBE_MUTATIONS} from './adobe';
import { MonoMenuModuleState, makeMonoMenuModule } from './mono-menu';
import { MealBuilderV2ModuleState, makeMealBuilderV2Module } from './meal-builder-v2';
import { appGetters } from './appGetters';
import { AdobeTargetModuleState, makeAdobeTargetModule } from './adobe-target';

export const VUEX_LOCAL_STORAGE_KEY = 'cmg-vuex';
const MAX_PERSIST_STATE_ATTEMPTS = 1;

export interface AppState {
  cms: CmsModuleState;
  customer: CustomerModuleState;
  findAChipotle: FindAChipotleModuleState;
  menu: MenuModuleState;
  restaurant: RestaurantModuleState;
  order: OrderModuleState;
  ui: UiModuleState;
  mealBuilder: MealBuilderModuleState;
  checkout: CheckoutModuleState;
  retroCredit: RetroCreditModuleState;
  reward: RewardModuleState;
  featureFlags: FeatureFlagsModuleState;
  adobe: AdobeModuleState;
  monoMenu: MonoMenuModuleState;
  mealBuilderV2: MealBuilderV2ModuleState;
  adobeTarget: AdobeTargetModuleState;
}

// tslint:disable-next-line
export const AppStorePlugin: PluginObject<void> = {
  install: vue => {
    vue.mixin({
      beforeCreate() {
        const store = this.$store;
        if (store) {
          this.$appStore = store;
        } else if (this.$options.parent && this.$options.parent.$store) {
          this.$appStore = this.$options.parent.$store;
        }
      },
    });
  },
};

export function buildStore(config: Config): Store<AppState> {
  const domEvent = new Event("cmg-vuex-persisted-state-change");

  return new Vuex.Store({
    modules: {
      cms: makeCmsModule(config),
      customer: makeCustomerModule(),
      findAChipotle: makeFindAChipotleModule(),
      restaurant: makeRestaurantModule(),
      menu: makeMenuModule(),
      order: makeOrderModule(),
      ui: makeUiModule(),
      mealBuilder: makeMealBuilderModule(),
      checkout: makeCheckoutModule(),
      retroCredit: makeRetroCreditModule(),
      reward: makeRewardModule(),
      featureFlags: makeFeatureFlagsModule(),
      adobe: makeAdobeModule(),
      monoMenu: makeMonoMenuModule(),
      mealBuilderV2: makeMealBuilderV2Module(),
      adobeTarget: makeAdobeTargetModule()
    },
    getters: appGetters,
    plugins: [
      createPersistedState({
        key: VUEX_LOCAL_STORAGE_KEY,
        // Anything in 'paths' will be persisted, BUT ONLY if the state gets set as part of one of the mutations in the filter function below
        paths: [
          'customer.jwt',
          'customer.loyalty',
          'customer.sustainabilityMetrics',
          'customer.celebration',
          'restaurant.selectedRestaurant',
          'menu.cachedMenus',
          'menu.cachedMenuRestaurantIds',
          'menu.universalMenu',
          'menu.metadata',
          'menu.metadataETag',
          'menu.cachedMetadata',
          'menu.preConfiguredMeals',
          'order.selectedDeliveryAddress',
          'order.cachedDeliveryAddress',
          'order.cachedRestaurant',
          'order.checkingOut',
          'order.deliveryOnly',
          'order.promoCode',
          'order.pendingOrder',
          'order.pendingGroupOrder',
          'order.groupOrderParticipantName',
          'order.groupOrderMealId',
          'order.isGroupOrderHost',
          'order.lastOrder',
          'order.upsellMeal',
          'checkout.sustainability',
          'adobe.linkClickedEvent',
          'adobeTarget.displayedUpsellItems',
        ],
        filter(mutation) {
          return [
            CUSTOMER_MUTATIONS.SET_JWT,
            CUSTOMER_MUTATIONS.SET_LOYALTY_FORCED,
            CUSTOMER_MUTATIONS.SET_LOYALTY_DECLINED,
            CUSTOMER_MUTATIONS.SET_CELEBRATION_NUMBER_OF_REWARDS,
            CUSTOMER_MUTATIONS.SET_CELEBRATION_POINTS,
            CUSTOMER_MUTATIONS.SET_CELEBRATION_LAST_UPDATED,
            CUSTOMER_MUTATIONS.SET_SUSTAINABILITY_HISTORY_METRICS,
            CUSTOMER_MUTATIONS.SET_SUSTAINABILITY_ORDER_METRICS,
            CUSTOMER_MUTATIONS.SET_SUSTAINABILITY_TOOLTIP_APPEARED,
            RESTAURANT_MUTATIONS.SET_SELECTED_RESTAURANT,
            MENU_MUTATIONS.CACHE_MENU_FOR_RESTAURANT,
            MENU_MUTATIONS.SET_CACHED_MENU_RESTAURANT_IDS,
            MENU_MUTATIONS.CACHE_UNIVERSAL_MENU,
            MENU_MUTATIONS.SET_METADATA,
            MENU_MUTATIONS.SET_PRECONFIGURED_MEALS,
            ORDER_MUTATIONS.SET_GROUP_ORDER,
            ORDER_MUTATIONS.SET_CACHED_RESTAURANT,
            ORDER_MUTATIONS.SET_CACHED_DELIVERY_ADDRESS,
            ORDER_MUTATIONS.SET_GROUP_ORDER_MEAL_IDS,
            ORDER_MUTATIONS.SET_GROUP_ORDER_PARTICIPANT_NAME,
            ORDER_MUTATIONS.SET_IS_GROUP_ORDER_HOST,
            ORDER_MUTATIONS.SET_ORDER,
            ORDER_MUTATIONS.SET_LAST_ORDER,
            ORDER_MUTATIONS.SET_UPSELL_MEAL,
            ORDER_MUTATIONS.SET_PROMO_CODE,
            ORDER_MUTATIONS.SET_SELECTED_DELIVERY_ADDRESS,
            CHECKOUT_MUTATIONS.SET_SUSTAINABILITY_ERROR,
            ADOBE_MUTATIONS.SET_LINK_CLICKED_EVENT,
            ADOBE_MUTATIONS.SET_REGISTRATION_COMPLETE_EVENT,
            ADOBE_MUTATIONS.SET_REWARDS_ENROLL_EVENT,
          ].includes(mutation.type);
        },
        getState(key, storage) {
          const state = JSON.parse(storage.getItem(key) as any) as AppState | undefined;

          // Since menu data is so massive, letting Vuex observe all the data is very expensive (and we don't even need it to!)
          const menusToFreeze = [
            state && state.menu && state.menu.activeMenu,
            state && state.menu && state.menu.universalMenu && state.menu.universalMenu.menu,
            ...Object.values((state && state.menu && state.menu.cachedMenus) || {}).map(entry => entry.menu),
          ];

          for (const menu of menusToFreeze) {
            if (!menu) {
              continue;
            }

            freezeMenu(menu);
          }

          return state;
        },
        setState(key, state, storage) {
          const appState = (state as any) as DeepPartial<AppState>;
          
          doCache(0, MAX_PERSIST_STATE_ATTEMPTS);
          
          function doCache(depth: number, maxDepth: number) {
            if (depth >= maxDepth) {
              return;
            }
            
            try {
              storage.setItem(key, JSON.stringify(appState));
              window.dispatchEvent(domEvent);
            } catch (e) {
              let shouldClear = true;
              if (config.isLocal) {
                shouldClear = confirm(`We just hit max application storage! Clear storage? (If you don't clear storage you can examine what caused the issue)`);
              }

              if (shouldClear) {
                if (appState.menu) {
                  appState.menu.cachedMenus = {};
                }
              }

              doCache(depth + 1, maxDepth);
            }
          }
        },
      }),
    ],
  });
}

export function resetStore() {
  localStorage.removeItem(VUEX_LOCAL_STORAGE_KEY);
}
